import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, cylShort, todayStr } from "@/lib/format";
import { BillingReminder } from "@/components/BillingReminder";
import { DateField } from "@/components/DateField";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Banknote, Smartphone, Boxes, AlertTriangle, RefreshCw, Phone, FileWarning, Landmark,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, Cell } from "recharts";

const Kpi = ({ icon: Icon, label, value, sub, tone = "", testid }) => (
  <Card className={`p-4 md:p-5 ${tone}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </div>
    <div className="mt-2 kpi-number text-2xl md:text-3xl font-semibold tabular-nums">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/dashboard?date=${date}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [date]);

  const k = data?.kpi || {};
  const chartData = (data?.drivers || []).map((d) => ({ name: d.driver_name.replace("Driver ", ""), Cash: d.cash_total, UPI: d.upi_total }));

  return (
    <div className="space-y-6 fade-up">
      {user?.role === "owner" && <BillingReminder />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold">Control Dashboard</h1>
          <p className="text-sm text-muted-foreground">Live view of collections, cylinders and red flags.</p>
        </div>
        <div className="flex items-center gap-2">
          <DateField value={date} onChange={setDate} className="w-auto" testid="dashboard-date-input" />
          <Button variant="outline" size="icon" onClick={load} data-testid="dashboard-refresh-button"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Banknote} label="Cash collected" value={inr(k.cash_total)} testid="owner-dashboard-kpi-today-cash" />
        <Kpi icon={Smartphone} label="UPI collected" value={inr(k.upi_total)} testid="owner-dashboard-kpi-today-upi" />
        <Kpi icon={Boxes} label="Cylinders out / delivered" value={`${k.total_loaded || 0} / ${k.total_delivered || 0}`} sub={`${k.total_returned || 0} returned`} />
        <Kpi icon={AlertTriangle} label="Red flags" value={k.red_flags || 0} sub={`${k.nonconsumer_unallocated || 0} non-consumer to allocate`} tone={k.red_flags ? "border-rose-200 bg-rose-50/60" : ""} testid="owner-dashboard-kpi-redflags" />
      </div>

      <Card className="p-4 md:p-5" data-testid="owner-dashboard-bank-deposit">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Landmark className="h-5 w-5" /></div>
            <div>
              <div className="text-xs font-medium text-muted-foreground">Cash deposited in bank</div>
              <div className="kpi-number text-2xl md:text-3xl font-semibold tabular-nums" data-testid="owner-dashboard-bank-deposited-value">{inr(k.bank_deposited || 0)}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-medium text-muted-foreground">Cash collected, not yet deposited</div>
            <div className={`text-xl md:text-2xl font-semibold tabular-nums ${(k.cash_undeposited || 0) > 0.5 ? "text-rose-600" : "text-emerald-600"}`} data-testid="owner-dashboard-cash-undeposited-value">{(k.cash_total || 0) > 0 ? inr(k.cash_undeposited || 0) : "—"}</div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-0 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="section_title text-lg font-semibold">Per-driver accountability</h2>
            <span className="text-xs text-muted-foreground">Total: {inr(k.collection_total)}</span>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="owner-dashboard-driver-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead className="text-right">Loaded</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Returned</TableHead>
                  <TableHead className="text-right">Cash</TableHead>
                  <TableHead className="text-right">UPI</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
                {!loading && (data?.drivers || []).map((d) => (
                  <TableRow key={d.driver_id} className={`cursor-pointer ${d.flags.length ? "bg-rose-50/70" : ""}`} onClick={() => setSel(d)} data-testid={`driver-row-${d.driver_id}`}>
                    <TableCell className="font-medium">{d.driver_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.total_loaded}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.total_delivered}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.total_returned}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(d.cash_total)}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(d.upi_total)}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {chartData.length > 0 && (
            <div className="p-4 border-t h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} width={50} />
                  <RTooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="Cash" stackId="a" fill="hsl(211 92% 43%)" radius={[0,0,0,0]} />
                  <Bar dataKey="UPI" stackId="a" fill="hsl(38 85% 55%)" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card className="p-4" data-testid="owner-dashboard-stock-panel">
            <h2 className="text-lg font-semibold flex items-center gap-2"><Boxes className="h-4 w-4 text-primary" /> Godown cylinder stock</h2>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              {(data?.cylinder_stock || []).map((s) => (
                <div key={s.cylinder_type} className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2" data-testid={`dashboard-stock-${s.cylinder_type}`}>
                  <span className="text-muted-foreground">{cylShort(s.cylinder_type)}</span>
                  <span className="font-semibold tabular-nums">{s.stock}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-4" data-testid="owner-dashboard-redflags-panel">
            <h2 className="text-lg font-semibold flex items-center gap-2"><FileWarning className="h-4 w-4 text-rose-600" /> Red flags</h2>
            <div className="mt-3 space-y-2">
              {(data?.drivers || []).filter((d) => d.flags.length).length === 0 && (
                <p className="text-sm text-muted-foreground">No mismatches today. All clear.</p>
              )}
              {(data?.drivers || []).flatMap((d) => d.flags.map((f, i) => (
                <div key={d.driver_id + i} className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm">
                  <div className="font-medium text-rose-900">{d.driver_name}</div>
                  <div className="text-rose-800">{f}</div>
                </div>
              )))}
            </div>
          </Card>
          <Card className="p-4">
            <h2 className="text-lg font-semibold">Office activity</h2>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              {Object.entries(data?.office_counts || {}).map(([k2, v]) => (
                <div key={k2} className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2">
                  <span className="text-muted-foreground">{k2.replace("_", "-")}</span>
                  <span className="font-semibold tabular-nums">{v}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{sel?.driver_name} — {sel?.date}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-secondary p-3"><div className="text-xs text-muted-foreground">Loaded</div><div className="text-xl font-semibold tabular-nums">{sel.total_loaded}</div></div>
                <div className="rounded-lg bg-secondary p-3"><div className="text-xs text-muted-foreground">Delivered</div><div className="text-xl font-semibold tabular-nums">{sel.total_delivered}</div></div>
                <div className="rounded-lg bg-secondary p-3"><div className="text-xs text-muted-foreground">Returned</div><div className="text-xl font-semibold tabular-nums">{sel.total_returned}</div></div>
              </div>
              <div className="text-sm">
                <div className="flex justify-between py-1"><span className="text-muted-foreground">Unaccounted cylinders</span><span className={`font-semibold ${sel.unaccounted ? "text-rose-600" : ""}`}>{sel.unaccounted}</span></div>
                <div className="flex justify-between py-1"><span className="text-muted-foreground">Cash</span><span className="font-semibold">{inr(sel.cash_total)}</span></div>
                <div className="flex justify-between py-1"><span className="text-muted-foreground">UPI</span><span className="font-semibold">{inr(sel.upi_total)}</span></div>
                <div className="flex justify-between py-1"><span className="text-muted-foreground">Non-consumer sales</span><span className="font-semibold">{sel.nonconsumer_count} ({inr(sel.nonconsumer_amount)})</span></div>
                <div className="flex justify-between py-1"><span className="text-muted-foreground">Empties collected</span><span className="font-semibold">{sel.empties}</span></div>
              </div>
              <div className="text-xs text-muted-foreground">
                By type: {Object.entries(sel.loaded).map(([c, q]) => `${cylShort(c)} ${sel.delivered[c]}/${q}`).join("  ·  ")}
              </div>
              {sel.flags.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                  {sel.flags.map((f, i) => <div key={i}>• {f}</div>)}
                </div>
              )}
              <a href={`tel:${sel.driver_mobile}`} className="inline-flex items-center gap-2 text-sm text-primary"><Phone className="h-4 w-4" /> Call {sel.driver_mobile}</a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
